/* @flow */
import React from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router';
import { observer } from 'mobx-react';
import { defineMessages, FormattedMessage } from 'react-intl';

import computed from 'lib/mobx/computed';
import URLBuilderable from 'lib/url/url-builderable';
import Styled from 'styled';
import { Form, Field } from 'lib/form/form';
import withDialog from 'lib/alerts/with-dialog';
import { RetryDialog } from 'lib/alerts/dialog';
import SVGIconView from 'lib/images/svg-icon-view';

import { AuthOrigin } from 'four-nets/user/auth/auth-ga-events';
import LoadingWrapperView from 'four-nets/ui/loaders/loading-wrapper-view';
import SubmitButtonView from 'four-nets/ui/submit-button/submit-button-view';
import newArticle from 'four-nets/icons/svg/icon_new-article.svg';
import { ValueFieldData, RequiredValidator } from 'lib/form/value-field-data';
import TextAreaView from 'four-nets/ui-v2/form/text-area/text-area';
import UserMentionable from 'four-nets/user/user-mentionable';
import NewCommentView, { NewCommentViewModel } from 'four-nets/common/comment/new-comment-view';
import defaultStyles from './new-blog-comment-view.scss';

export { NewBlogCommentViewModel };

@UserMentionable()
class NewBlogCommentViewModel extends NewCommentViewModel {
    form: Form;
    post: Post;
    refresh: Function;

    @computed get userMentionableField() {
        return this.form.field.get('content');
    }

    constructor(options: { post: Post, refresh: Function, intl: any }) {
        const { post, refresh, intl } = options;

        super({
            user: () => post.userStore.me,
        });

        this.post = post;
        this.refresh = refresh;
        this.intl = intl;

        this.form = new Form({
            field: {
                content: new Field({
                    data: new ValueFieldData({
                        validators: [new RequiredValidator()],
                    }),
                }),
            },
            handleSubmit: this.handleSubmit,
        });
    }

    handleSubmit = ({ data }) => {
        const { content } = data;
        return this.post.createComment({
            content,
        });
    };

    @computed get title() {
        return this.intl.formatMessage(defineMessages({ ADD_COMMENT: { id: 'ADD_COMMENT' } }).ADD_COMMENT, {
            gender: 'v',
        });
    }
}

@URLBuilderable()
@withDialog
@Styled(defaultStyles)
@observer
class NewBlogCommentView extends React.Component {
    textArea: ?TextAreaView;

    static contextTypes = {
        authCheck: PropTypes.func,
    };

    submit = () => {
        const {
            newBlogCommentViewModel: { post, form },
        } = this.props;

        this.context
            .authCheck(form.submit, AuthOrigin.NewComment)
            .then(data => {
                form.reset();
                this.props.history.push(`/magazine/${post.slug}/?id=${data.id}`);
            })
            .catch(err => {
                this.props.showDialog(
                    new RetryDialog({
                        retry: this.submit,
                        message: err.message,
                    })
                );
            });

        return null;
    };

    render() {
        const { newBlogCommentViewModel, styles } = this.props;
        const { form } = newBlogCommentViewModel;

        return (
            <NewCommentView
                newCommentViewModel={newBlogCommentViewModel}
                title={<FormattedMessage id='ADD_GROUP_POST' />}
            >
                <LoadingWrapperView loading={form.submitting}>
                    <div className={styles.newComment}>
                        <div className={styles.textArea}>
                            <div className={styles.textAreaWrapper}>
                                <TextAreaView
                                    field={form.field.get('content')}
                                    emojify={true}
                                    innerRef={this.setTextArea}
                                    onFocus={this.onFocus}
                                    onBlur={this.onBlur}
                                    scrollToOnChange={true}
                                    width={575}
                                />
                            </div>
                        </div>
                        <div className={styles.submit}>
                            <span className={styles.buttonWrapper}>
                                <SubmitButtonView
                                    icon={<SVGIconView src={newArticle} />}
                                    disabled={!form.valid || form.submitting}
                                    onClick={this.submit}
                                >
                                    <FormattedMessage id='ADD_COMMENT' values={{ gender: 'v' }} />
                                </SubmitButtonView>
                            </span>
                        </div>
                    </div>
                </LoadingWrapperView>
            </NewCommentView>
        );
    }
}

export default withRouter(NewBlogCommentView);
